import { Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert } from '@sama/js-core';
import { every, isEqual, pick } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { debounceTime, exhaustMap } from 'rxjs/operators';
import { NavigationWarningService } from '../navigation/navigation-warning.service';
import { BaseSaveManagerService } from './base-save-manager.service';
import { ExecutionQueueService } from './execution-queue.service';

/**
 * Note this also handles navigation confirmation modals if the page has unsaved
 * changes
 */
@Injectable()
export class DebounceSaveManagerService<T> extends InjectableSuperclass {
  private masterStore!: Store<T>;
  private workingCopyStore!: Store<T>;
  private saveFn!: () => Promise<any>;
  private editableFields!: Array<keyof T>;
  private inFlightSave?: Promise<any>;

  constructor(
    private baseSaveManager: BaseSaveManagerService,
    private executionQueue: ExecutionQueueService,
    private navigationWarning: NavigationWarningService,
  ) {
    super();
  }

  // `saveFunction` should return a promise that resolves to the updated
  // `master` from the server, after the save
  // eslint-disable-next-line max-params-unless-injectable -- if you need to modify this code, fix this first
  initialize(
    injectable: InjectableSuperclass,
    masterStore: Store<T>,
    workingCopyStore: Store<T>,
    saveFunction: () => Promise<T>,
    workingCopyFields: Array<keyof T>,
  ) {
    assert(!this.saveFn, 'this should only be used once per page');

    this.masterStore = masterStore;
    this.workingCopyStore = workingCopyStore;
    this.saveFn = saveFunction;

    this.editableFields = workingCopyFields;

    this.updateWorkingCopy();
    this.startSaves();
    this.navigationWarning.register(() => this.hasChanges(), injectable);
  }

  saveAndThen(fn: () => any) {
    this.enqueueSaveAction(() => this.saveIfChanged().then(fn));
  }

  updateWorkingCopy() {
    this.workingCopyStore.set(
      pick(this.masterStore.state(), ...this.editableFields) as T,
    );
  }

  private startSaves() {
    this.subscribeTo(
      this.workingCopyStore.$.pipe(
        debounceTime(1000),
        exhaustMap(() => this.inFlightSave || Promise.resolve()),
      ),
      () => {
        this.enqueueSaveAction(this.saveIfChanged.bind(this));
      },
    );
  }

  private enqueueSaveAction(saveFn: () => Promise<any> | undefined) {
    this.inFlightSave = this.executionQueue.enqueue(saveFn).finally(() => {
      this.inFlightSave = undefined;
    });
  }

  private saveIfChanged() {
    if (!this.hasChanges()) {
      return Promise.resolve();
    }

    return this.baseSaveManager.withSaveIndicator(
      () =>
        this.saveFn().then((newMaster) => {
          this.masterStore.set(newMaster);
        }),
      false,
    );
  }

  private hasChanges() {
    const master = this.masterStore.state();
    const workingCopy = this.workingCopyStore.state();
    return (
      this.baseSaveManager.showIsSaving ||
      !every(workingCopy, (v, k) => isEqual(master[k as keyof T], v))
    );
  }
}
